const passwordMinLength = 12;
const passwordBlockedChars = ' ›‡‹°₹‚ﬂ±Ú˘¯¿';
const passwordAllowedSpecialChars =
  '! @ £ $ % _ - ( ) [ ] ~ ; : ? . ^ & * + = ` # \' < > / \\ | { } , " € ¬ ¦';

// Convert special chars string to regex-safe string by escaping special characters
// Sort characters to avoid range issues in character class
const escapedSpecialChars = passwordAllowedSpecialChars
  .split('')
  .sort()
  .join('')
  .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Convert blocked chars to regex-safe string
const escapedBlockedChars = passwordBlockedChars
  .split('')
  .sort()
  .join('')
  .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const validationMessages = {
  nameValidationError:
    'Only letters, hyphens, apostrophes and spaces may be entered in this field.',
  passwordValidationError: `Your password needs to be at least ${passwordMinLength} characters, contain an uppercase letter, a lowercase letter, a number and one of the following symbols: ${passwordAllowedSpecialChars}`,
  passwordValidationErrorSpecialChars: `At least one of the following symbols: ${passwordAllowedSpecialChars}`,
  lowercase: 'At least 1 lower case letter',
  capital: 'At least 1 upper case letter',
  number: 'A number',
  minLength: `At least ${passwordMinLength} characters`,
  phoneNumberValidationError:
    'Phone number must be at least 8 digits long and contain only numbers or following symbols: + ( )',
  iban: 'Only letters, numbers and dashes are allowed in this field',
};

export const validationRegexes = {
  password: new RegExp(
    `^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[${escapedSpecialChars}])[A-Za-z\\d${escapedSpecialChars}]{${passwordMinLength},}$`,
    'i',
  ),
  userName: /^[a-zA-Z'\- ]+$/i,
  email:
    /^[A-Z0-9][A-Z0-9_%+-]*(\.[A-Z0-9_%+-]+)*@[A-Z0-9]([A-Z0-9-]*[A-Z0-9])?(\.[A-Z0-9]([A-Z0-9-]*[A-Z0-9])?)+$/i,
  phoneNumber: /^(?=(?:\D*\d){8,})[ ()\d+]+$/i,
  ukSortCode: /^\d{2}-\d{2}-\d{2}$/,
  ukBankAccountNumber: /^\d{8}$/,
  iban: /^[a-z0-9\- ]+$/i,
};

export const validatePassword = (password: string) => {
  if (!validationRegexes.password.test(password)) {
    return validationMessages.passwordValidationError;
  }
  return false;
};

export const validateUserName = (name: string) => {
  if (!validationRegexes.userName.test(name)) {
    return validationMessages.nameValidationError;
  }
  return false;
};

export const isValidEmail = (email: string) => {
  if (validationRegexes.email.test(email)) {
    return true;
  }
  return false;
};

export const validateUkSortCode = (sortCode: string) => {
  if (validationRegexes.ukSortCode.test(sortCode)) {
    return true;
  }
  return false;
};

export interface PasswordValidationResult {
  isValid: boolean;
  checks: {
    specialChar: boolean;
    number: boolean;
    capital: boolean;
    lowercase: boolean;
    minLength: boolean;
  };
}

export const validatePasswordRequirements = (
  password?: string,
): PasswordValidationResult => {
  const checks = {
    specialChar: false,
    number: false,
    capital: false,
    lowercase: false,
    minLength: false,
  };

  if (!password) {
    return { isValid: false, checks };
  }

  // has uppercase letter
  checks.capital = password.toLowerCase() !== password;

  // has lowercase letter
  checks.lowercase = password.toUpperCase() !== password;

  // has number
  checks.number = /\d/.test(password);

  // has special character
  const blockList = new RegExp(`[${escapedBlockedChars}]`);
  if (!blockList.test(password)) {
    checks.specialChar = new RegExp(`[${escapedSpecialChars}]`).test(password);
  }

  // has minimum length
  checks.minLength = password.length >= passwordMinLength;

  return {
    isValid:
      checks.specialChar &&
      checks.number &&
      checks.capital &&
      checks.lowercase &&
      checks.minLength,
    checks,
  };
};
